import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  TablePagination,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";

import adjustSvg from "assets/folders/notes.svg";

import {
  fetchEmployeeLeaveBalances,
  fetchLeaveAdjustmentHistory,
} from "store/features/company/leaveManagement/leaveAdjustmentReimbursement/leaveAdjustmentReimbursementActions";
import {
  selectEmployeeBalances,
  selectAdjustmentHistory,
  selectLoadingBalances,
  selectLoadingHistory,
} from "store/features/company/leaveManagement/leaveAdjustmentReimbursement/leaveAdjustmentReimbursementSelectors";
import AdjustLeaveModal from "./AdjustLeaveModal";

const LeaveAdjustmentReimbursement = () => {
  const dispatch = useDispatch();

  const employeeBalances = useSelector(selectEmployeeBalances);
  const adjustmentHistory = useSelector(selectAdjustmentHistory);
  const loadingBalances = useSelector(selectLoadingBalances);
  const loadingHistory = useSelector(selectLoadingHistory);

  const [activeTab, setActiveTab] = useState(0); // 0: Balances Overview, 1: Adjustment History
  const [searchTerm, setSearchTerm] = useState("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBalanceRecord, setSelectedBalanceRecord] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchEmployeeLeaveBalances());
    dispatch(fetchLeaveAdjustmentHistory());
  }, [dispatch]);

  // Unique leave types for dropdown filter
  const leaveTypesList = useMemo(() => {
    const typesMap = {};
    employeeBalances.forEach((b) => {
      if (b.leave_type_id) {
        typesMap[b.leave_type_id] = b.leave_type_name;
      }
    });
    return Object.entries(typesMap).map(([id, name]) => ({ id, name }));
  }, [employeeBalances]);

  // Filtered Balances
  const filteredBalances = useMemo(() => {
    return employeeBalances.filter((item) => {
      const fullName = `${item.first_name || ""} ${
        item.last_name || ""
      }`.toLowerCase();
      const code = (item.employee_code || "").toLowerCase();
      const search = searchTerm.toLowerCase();

      const matchesSearch = fullName.includes(search) || code.includes(search);
      const matchesLeaveType =
        leaveTypeFilter === "all" ||
        String(item.leave_type_id) === String(leaveTypeFilter);

      return matchesSearch && matchesLeaveType;
    });
  }, [employeeBalances, searchTerm, leaveTypeFilter]);

  // Filtered History
  const filteredHistory = useMemo(() => {
    return adjustmentHistory.filter((item) => {
      const fullName = `${item.first_name || ""} ${
        item.last_name || ""
      }`.toLowerCase();
      const code = (item.employee_code || "").toLowerCase();
      const search = searchTerm.toLowerCase();

      const matchesSearch = fullName.includes(search) || code.includes(search);
      const matchesAction =
        actionFilter === "all" ||
        (actionFilter === "credit" &&
          (item.action === "credit" || item.action === "add")) ||
        (actionFilter === "debit" &&
          (item.action === "debit" || item.action === "deduct"));
      const matchesLeaveType =
        leaveTypeFilter === "all" ||
        String(item.leave_type_id) === String(leaveTypeFilter);

      return matchesSearch && matchesAction && matchesLeaveType;
    });
  }, [adjustmentHistory, searchTerm, actionFilter, leaveTypeFilter]);

  const handleOpenAdjustModal = (balanceRecord = null) => {
    setSelectedBalanceRecord(balanceRecord);
    setModalOpen(true);
  };

  // Excel Export
  const handleExportExcel = () => {
    const dataToExport =
      activeTab === 0
        ? filteredBalances.map((b) => ({
            "Employee Code": b.employee_code || "",
            "Employee Name": `${b.first_name} ${b.last_name}`,
            Department: b.department || "",
            "Leave Type": b.leave_type_name,
            "Policy Max Limit (Annual)": b.max_days_per_year || "Unlimited",
            "Total Allocated Days": b.total_days,
            "Used Days": b.used_days,
            "Pending Days": b.pending_days,
            "Available Balance": b.available_days,
          }))
        : filteredHistory.map((h) => ({
            "Date Logged": dayjs(h.created_at).format("YYYY-MM-DD HH:mm"),
            "Employee Code": h.employee_code || "",
            "Employee Name": `${h.first_name} ${h.last_name}`,
            "Leave Type": h.leave_type_name,
            Action:
              h.action === "credit" || h.action === "add"
                ? "ADD LEAVE"
                : "DEDUCT LEAVE",
            "Days Adjusted": h.days,
            "Previous Total Allocation": h.previous_total_days,
            "New Total Allocation": h.new_total_days,
            "Previous Used Days": h.previous_used_days,
            "New Used Days": h.new_used_days,
            Reason: h.reason || "-",
            "Adjusted By": h.adjusted_by_name || "Admin",
          }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      activeTab === 0 ? "Leave Balances" : "Adjustment Logs",
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(
      blob,
      `Leave_${
        activeTab === 0 ? "Balances" : "Adjustment_Logs"
      }_${dayjs().format("YYYY-MM-DD")}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={adjustSvg}
              alt="Leave Adjustment"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Leave Adjustment & Reimbursement
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage employee leave balances, adjustments and reimbursement
                logs
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Main Table Container */}
      <Paper sx={{ borderRadius: 1, overflow: "hidden", mb: 3 }}>
        {/* Header Tabs & Controls Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, val) => {
              setActiveTab(val);
              setPage(0);
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              icon={<WalletIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Employee Leave Balances"
            />
            <Tab
              icon={<HistoryIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Adjustment Logs"
            />
          </Tabs>

          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            alignItems="center"
          >
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search employee or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />

            {/* Leave Type Filter */}
            <TextField
              select
              size="small"
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All Leave Types</MenuItem>
              {leaveTypesList.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Action Filter (Only for history tab) */}
            {activeTab === 1 && (
              <TextField
                select
                size="small"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="credit">Add Leave</MenuItem>
                <MenuItem value="debit">Deduct Leave</MenuItem>
              </TextField>
            )}

            {/* Export Excel Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ fontWeight: 600, height: 40 }}
            >
              Export
            </Button>
          </Stack>
        </Box>

        {/* Tab 0: Employee Leave Balances Table */}
        {activeTab === 0 && (
          <TableContainer>
            {loadingBalances ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : filteredBalances.length === 0 ? (
              <Box sx={{ p: 5, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body1">
                  No employee leave balance records found.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead sx={{ bgcolor: "background.neutral" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Policy Annual Limit
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Total Allocation
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Used Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pending Days</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Available Balance
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBalances
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => (
                      <TableRow
                        key={`${row.employee_id}-${row.leave_type_id}-${idx}`}
                        hover
                      >
                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            {row.first_name} {row.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.employee_code || "EMP"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={row.department || "N/A"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={row.leave_type_name}
                            size="small"
                            sx={{
                              bgcolor:
                                (row.leave_type_color || "#3b82f6") + "20",
                              color: row.leave_type_color || "#3b82f6",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {row.max_days_per_year > 0
                            ? `${row.max_days_per_year} Days`
                            : "Unlimited"}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 700 }}>
                          {row.total_days} Days
                        </TableCell>
                        <TableCell color="error.main">
                          {row.used_days} Days
                        </TableCell>
                        <TableCell color="warning.main">
                          {row.pending_days} Days
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={`${row.available_days} Days`}
                            size="small"
                            color={
                              parseFloat(row.available_days) > 0
                                ? "success"
                                : "default"
                            }
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<TuneIcon />}
                            onClick={() => handleOpenAdjustModal(row)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              textTransform: "none",
                            }}
                          >
                            Adjust Leave
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={filteredBalances.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        )}

        {/* Tab 1: Adjustment Audit Log Table */}
        {activeTab === 1 && (
          <TableContainer>
            {loadingHistory ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : filteredHistory.length === 0 ? (
              <Box sx={{ p: 5, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body1">
                  No adjustment audit logs found.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead sx={{ bgcolor: "background.neutral" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date Logged</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Leave Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Days Changed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Balance Transition
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      Reason / Remarks
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Adjusted By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {dayjs(log.created_at).format("YYYY-MM-DD HH:mm")}
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            {log.first_name} {log.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.employee_code || "EMP"}
                          </Typography>
                        </TableCell>

                        <TableCell>{log.leave_type_name}</TableCell>

                        <TableCell>
                          <Chip
                            label={
                              log.action === "credit" || log.action === "add"
                                ? "ADD LEAVE"
                                : "DEDUCT LEAVE"
                            }
                            size="small"
                            color={
                              log.action === "credit" || log.action === "add"
                                ? "success"
                                : "error"
                            }
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>

                        <TableCell sx={{ fontWeight: 700 }}>
                          {log.days} Days
                        </TableCell>

                        <TableCell>
                          <Typography variant="caption" display="block">
                            Total Quota: {log.previous_total_days} &rarr;{" "}
                            {log.new_total_days}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Used Days: {log.previous_used_days} &rarr;{" "}
                            {log.new_used_days}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" noWrap title={log.reason}>
                            {log.reason || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {log.adjusted_by_name || "HR Admin"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={filteredHistory.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Adjust Leave Modal */}
      <AdjustLeaveModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedBalance={selectedBalanceRecord}
      />
    </Box>
  );
};

export default LeaveAdjustmentReimbursement;
